import {
    booleanAttribute,
    Component,
    ElementRef,
    forwardRef,
    HostBinding,
    inject,
    Input,
    input,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core'
import { NavItem } from '../../../../core/interfaces/nav/nav-item.interface'
import { SidebarNavTitleComponent } from '../sidebar-nav-title/sidebar-nav-title.component'
import { SidebarNavLinkComponent } from '../sidebar-nav-link/sidebar-nav-link.component'
import { SidebarNavItemClassPipe } from '../sidebar-nav-item-class.pipe'
import { NgClass, NgTemplateOutlet } from '@angular/common'
import { Router, NavigationEnd, RouterModule } from '@angular/router'
import { filter, Observable, Subscription } from 'rxjs'
import { HtmlAttributesDirective } from '../../../directives/html-attributes.directive'
import { SidebarNavBadgePipe } from '../sidebar-nav-badge.pipe'
import { SidebarNavGroupService } from '../sidebar-nav-group.service'
import { SidebarNavIconPipe } from '../sidebar-nav-icon.pipe'
import { SidebarNavHelper } from '../sidebar-nav.service'
import { SidebarNavLabelComponent } from '../sidebar-nav-label/sidebar-nav-label.component'
import { SidebarNavDividerComponent } from '../sidebar-nav-divider/sidebar-nav-divider.component'
import { SidebarComponent } from '../sidebar.component'
import { SidebarService } from '../sidebar.service'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'

@Component({
    selector: 'sq-sidebar-nav-group',
    templateUrl: './sidebar-nav-group.component.html',
    styleUrls: ['./sidebar-nav-group.component.scss'],
    providers: [SidebarNavHelper, SidebarNavGroupService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FontAwesomeModule,
        HtmlAttributesDirective,
        // IconDirective,
        NgTemplateOutlet,
        NgClass,
        SidebarNavIconPipe,
        SidebarNavBadgePipe,
        forwardRef(() => SidebarNavComponent),
    ],
})
export class SidebarNavGroupComponent implements OnInit, OnDestroy {
    readonly #router = inject(Router)
    readonly #renderer = inject(Renderer2)
    readonly #hostElement = inject(ElementRef)
    readonly #sidebarNavGroupService = inject(SidebarNavGroupService)
    public readonly helper = inject(SidebarNavHelper)

    constructor() {
        const router = this.#router

        this.navigationEndObservable = router.events.pipe(
            filter((event: any) => event instanceof NavigationEnd)
        ) as Observable<NavigationEnd>
    }

    @Input() item: any
    @Input() dropdownMode: 'path' | 'none' | 'close' = 'path'
    @Input() show?: boolean
    @Input({ transform: booleanAttribute }) compact?: boolean

    @HostBinding('class')
    get hostClasses(): any {
        return {
            'nav-group': true,
            show: this.open,
        }
    }

    @ViewChild(forwardRef(() => SidebarNavComponent), { read: ElementRef }) sidebarNav!: ElementRef

    navigationEndObservable: Observable<NavigationEnd>
    navSubscription!: Subscription
    navGroupSubscription!: Subscription

    public open!: boolean
    public navItems: NavItem[] = []
    public display: any = { display: 'none' }

    ngOnInit(): void {
        this.navItems = [...this.item.children]

        this.navSubscription = this.navigationEndObservable.subscribe((event: NavigationEnd) => {
            if (this.dropdownMode !== 'none') {
                const samePath = this.samePath(event.url)
                this.openGroup(samePath)
            }
        })

        if (this.samePath(this.#router.routerState.snapshot.url)) {
            this.openGroup(true)
        }

        this.navGroupSubscription = this.#sidebarNavGroupService.sidebarNavGroupState$.subscribe((next) => {
            if (this.dropdownMode === 'close' && next.sidebarNavGroup && next.sidebarNavGroup !== this) {
                if (next.sidebarNavGroup.item.url.startsWith(this.item.url)) {
                    return
                }
                if (this.samePath(this.#router.routerState.snapshot.url)) {
                    this.openGroup(true)
                    return
                }
                this.openGroup(false)
            }
        })
    }

    samePath(url: string): boolean {
        // console.log('item:', this.item.name, this.item.url, 'url:', url);
        const itemArray = this.item.url?.split('/')
        const urlArray = url.split('/')
        return itemArray?.every((value: string, index: number) => {
            // console.log(value === urlArray[index], 'value:', value, 'index:', index, urlArray[index], url);
            return value === urlArray[index]
        })
    }

    openGroup(open: boolean): void {
        this.open = open

        // this.display = open ? { display: 'block' } : { display: 'none' }
    }

    toggleGroup($event: any): void {
        $event.preventDefault()
        this.openGroup(!this.open)
        if (this.open) {
            this.#sidebarNavGroupService.toggle({ open: this.open, sidebarNavGroup: this })
        }
    }

    ngOnDestroy(): void {
        this.navSubscription?.unsubscribe()
    }
}

@Component({
    selector: 'sq-sidebar-nav',
    imports: [
        RouterModule,
        NgClass,
        HtmlAttributesDirective,
        SidebarNavTitleComponent,
        SidebarNavGroupComponent,
        SidebarNavLinkComponent,
        SidebarNavLabelComponent,
        SidebarNavDividerComponent,
        SidebarNavItemClassPipe,
    ],
    templateUrl: './sidebar-nav.component.html',
    styleUrl: './sidebar-nav.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [SidebarNavHelper],
})
export class SidebarNavComponent {
    readonly sidebar = inject(SidebarComponent, { optional: true })
    public readonly helper = inject(SidebarNavHelper)

    readonly router = inject(Router)
    readonly #renderer = inject(Renderer2)
    readonly #hostElement = inject(ElementRef)
    readonly #sidebarService = inject(SidebarService)

    navItems = input<NavItem[]>()
    @Input() dropdownMode: 'path' | 'none' | 'close' = 'path'
    @Input({ transform: booleanAttribute }) groupItems?: boolean
    @Input({ transform: booleanAttribute }) compact?: boolean

    @HostBinding('class')
    get hostClasses(): any {
        return {
            'sidebar-nav': !this.groupItems,
            'nav-group-items': this.groupItems,
            compact: this.groupItems && this.compact,
        }
    }

    // @HostBinding('class.nav-group-items')
    // get sidebarNavGroupItemsClass(): boolean {
    //   return !!this.groupItems;
    // }

    @HostBinding('attr.role')
    @Input()
    role = 'navigation'

    public navItemsArray: NavItem[] = []

    public ngOnChanges(changes: SimpleChanges): void {
        this.navItemsArray = Array.isArray(this.navItems) ? this.navItems.slice() : []
    }

    public hideMobile(): void {
        // todo: proper scrollIntoView() after NavigationEnd
        if (this.sidebar && this.sidebar.sidebarState.mobile) {
            this.#sidebarService.toggle({ toggle: 'visible', sidebar: this.sidebar })
        }
    }
}
